<template>
  <v-list density="compact" class="notifications-list bg-surface border border-white-05 rounded-xl" max-height="500">
    <div class="pa-4 d-flex align-center justify-space-between border-bottom border-white-05">
      <span class="text-subtitle-1 font-weight-black">Notifications</span>
      <div class="d-flex align-center ga-1">
        <v-btn
          v-if="notifications.length > 0"
          variant="text"
          size="x-small"
          color="primary"
          class="text-none"
          @click="handleMarkAllRead"
        >
          Mark all as read
        </v-btn>
        <v-btn
          icon="mdi-refresh"
          variant="text"
          size="x-small"
          :loading="loading"
          @click="fetchNotifications()"
        ></v-btn>
      </div>
    </div>

    <v-divider class="border-white-05"></v-divider>

    <div v-if="loading && notifications.length === 0" class="pa-10 text-center">
      <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
    </div>

    <div v-else-if="notifications.length === 0" class="pa-10 text-center text-grey">
      <v-icon icon="mdi-bell-off-outline" size="48" class="mb-4 opacity-20"></v-icon>
      <p>No notifications yet</p>
    </div>

    <template v-else>
      <v-list-item
        v-for="group in groupedNotifications"
        :key="group.items[0].uri"
        class="notification-item py-3 px-4 border-bottom border-white-01"
        :class="{ 'unread-item': !group.isRead }"
        @click="handleNotificationClick(group)"
      >
        <template v-slot:prepend>
          <div class="d-flex flex-column align-center mr-3 mt-1" style="min-width: 48px;">
            <v-icon :icon="getNotificationIcon(group.reason)" :color="getNotificationColor(group.reason)" size="20" class="mb-2"></v-icon>
            <div class="avatar-stack">
              <v-avatar 
                v-for="(author, idx) in group.authors.slice(0, 5)" 
                :key="author.did"
                size="28" 
                class="stacked-avatar border border-surface"
                :style="{ marginLeft: Number(idx) > 0 ? '-12px' : '0', zIndex: 10 - Number(idx) }"
                @click.stop="handleProfileClick(author.handle)"
              >
                <v-img v-if="author.avatar" :src="author.avatar"></v-img>
                <v-icon v-else icon="mdi-account" size="small"></v-icon>
              </v-avatar>
              <div v-if="group.authors.length > 5" class="more-authors text-caption text-grey ml-1">
                +{{ group.authors.length - 5 }}
              </div>
            </div>
          </div>
        </template>

        <v-list-item-title class="notification-title font-weight-medium mb-1">
          <span class="font-weight-bold">{{ getAuthorsText(group) }}</span>
          {{ getNotificationText(group) }}
        </v-list-item-title>
        
        <v-list-item-subtitle v-if="group.record?.text" class="notification-subtitle text-grey italic">
          "{{ group.record.text }}"
        </v-list-item-subtitle>
        
        <template v-slot:append>
          <div class="text-caption text-grey-darken-1 ml-2">
            {{ formatTime(group.latestIndexedAt) }}
          </div>
        </template>
      </v-list-item>

      <div v-if="cursor" class="pa-2 text-center">
        <v-btn
          variant="text"
          size="small"
          block
          class="text-none"
          :loading="loadingMore"
          @click="loadMore"
        >
          Load more
        </v-btn>
      </div>
    </template>
  </v-list>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { getNotifications, updateNotificationsSeen, currentUser } from '@/services/bluesky';

const router = useRouter();
const notifications = ref<any[]>([]);
const loading = ref(true);
const loadingMore = ref(false);
const cursor = ref<string | undefined>(undefined);

const emit = defineEmits(['close', 'view-post', 'view-profile']);

const fetchNotifications = async (isLoadMore = false) => {
  if (isLoadMore) {
    loadingMore.value = true;
  } else {
    loading.value = true;
  }

  const result = await getNotifications(40, cursor.value); // Fetch more for grouping
  
  if (isLoadMore) {
    notifications.value = [...notifications.value, ...result.notifications];
  } else {
    notifications.value = result.notifications;
  }
  
  cursor.value = result.cursor;
  loading.value = false;
  loadingMore.value = false;
};

const groupedNotifications = computed(() => {
  const result: any[] = [];
  const notificationsList = notifications.value;

  for (const n of notificationsList) {
    const rawReason = n.reason || '';
    const reason = rawReason.split('.').pop()?.toLowerCase() || '';
    const isGroupable = ['like', 'repost', 'follow'].some(r => reason.includes(r));
    
    if (isGroupable) {
      const existingGroup = result.find(g => {
        const gRawReason = g.reason || '';
        const gReason = gRawReason.split('.').pop()?.toLowerCase() || '';
        
        // Match if both are likes (standard or via-repost) and have same subject
        const matchesType = (gReason.includes('like') && reason.includes('like')) ||
                          (gReason.includes('repost') && reason.includes('repost')) ||
                          (gReason.includes('follow') && reason.includes('follow'));

        return matchesType && 
          (g.reasonSubject === n.reasonSubject || (reason.includes('follow') && !n.reasonSubject)) &&
          g.items.length < 50;
      });

      if (existingGroup) {
        if (!existingGroup.authors.find((a: any) => a.did === n.author.did)) {
          existingGroup.authors.push(n.author);
        }
        if (new Date(n.indexedAt) > new Date(existingGroup.latestIndexedAt)) {
          existingGroup.latestIndexedAt = n.indexedAt;
        }
        if (!n.isRead) existingGroup.isRead = false;
        existingGroup.items.push(n);
        continue;
      }
    }

    result.push({
      ...n,
      normalizedReason: reason,
      authors: [n.author],
      latestIndexedAt: n.indexedAt,
      items: [n],
      isRead: n.isRead
    });
  }
  return result;
});

const loadMore = () => {
  if (cursor.value) {
    fetchNotifications(true);
  }
};

const handleMarkAllRead = async () => {
  await updateNotificationsSeen();
  // locally update read status
  notifications.value = notifications.value.map(n => ({ ...n, isRead: true }));
};

const getAuthorDisplayName = (author: any) => {
  if (author.displayName) return author.displayName;
  return author.handle?.startsWith('did:') ? 'Someone' : (author.handle || 'Someone');
};

const getAuthorsText = (group: any) => {
  const authors = group.authors;
  if (!authors || authors.length === 0) return 'Someone';
  if (authors.length === 1) return getAuthorDisplayName(authors[0]);
  if (authors.length === 2) return `${getAuthorDisplayName(authors[0])} and ${getAuthorDisplayName(authors[1])}`;
  return `${getAuthorDisplayName(authors[0])} and ${authors.length - 1} others`;
};

const getNotificationIcon = (reason: string) => {
  const r = (reason || '').split('.').pop()?.toLowerCase() || '';
  if (r.includes('like')) return 'mdi-heart';
  if (r.includes('repost')) return 'mdi-repeat';
  if (r.includes('follow')) return 'mdi-account-plus';
  if (r.includes('mention')) return 'mdi-at';
  if (r.includes('reply')) return 'mdi-reply';
  if (r.includes('quote')) return 'mdi-format-quote-open';
  return 'mdi-bell';
};

const getNotificationColor = (reason: string) => {
  const r = (reason || '').split('.').pop()?.toLowerCase() || '';
  if (r.includes('like')) return 'error';
  if (r.includes('repost')) return 'success';
  if (r.includes('follow')) return 'primary';
  if (r.includes('mention')) return 'info';
  if (r.includes('reply')) return 'secondary';
  if (r.includes('quote')) return 'purple';
  return 'grey';
};

const getNotificationText = (group: any) => {
  const rawReason = (group.normalizedReason || group.reason || '').toLowerCase();
  const reason = rawReason.split('.').pop() || '';
  const isRepostSubject = group.reasonSubject?.includes('app.bsky.feed.repost') || rawReason.includes('via-repost');
  
  if (reason.includes('like')) return isRepostSubject ? 'liked your repost' : 'liked your post';
  if (reason.includes('repost')) return isRepostSubject ? 'reposted your repost' : 'reposted your post';
  if (reason.includes('follow')) return 'started following you';
  if (reason.includes('mention')) return 'mentioned you in a post';
  if (reason.includes('reply')) return 'replied to your post';
  if (reason.includes('quote')) return 'quoted your post';
  return reason ? `${reason}d you` : 'sent you a notification';
};

const formatTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return 'now';
};

const handleNotificationClick = (group: any) => {
  emit('close');
  
  const notification = group.items[0]; // Use the first item for data
  const rawReason = (notification.reason || '').toLowerCase();
  const normalizedReason = (group.normalizedReason || rawReason.split('.').pop() || '').toLowerCase();

  // Follow notifications lead to profile
  if (normalizedReason === 'follow' || rawReason.includes('follow')) {
    emit('view-profile', notification.author.handle);
    return;
  }

  // Determine the correct URI and Handle based on the reason
  let uri = undefined;
  let handle = undefined;

  if (normalizedReason === 'reply' || normalizedReason === 'quote' || normalizedReason === 'mention' ||
      rawReason.includes('reply') || rawReason.includes('quote') || rawReason.includes('mention')) {
    // For these, we want to view the actor's post (the reply/quote/mention itself)
    uri = notification.uri;
    handle = notification.author.handle;
  } else if (normalizedReason === 'like' || normalizedReason === 'repost' || 
             rawReason.includes('like') || rawReason.includes('repost')) {
    // For these, we want to view OUR post that was liked/reposted
    uri = notification.reasonSubject;
    if (currentUser.value) {
      handle = currentUser.value.handle;
    }
  } else {
    // Fallback
    uri = notification.reasonSubject || notification.uri;
  }

  if (uri) {
    const isPost = uri.includes('app.bsky.feed.post');
    const isNote = uri.includes('id.gamedev.notes');
    const isRepost = uri.includes('app.bsky.feed.repost');

    // If it's not a viewable record type, just go to the profile
    if (!isPost && !isNote && !isRepost) {
      emit('view-profile', notification.author.handle);
      return;
    }

    emit('view-post', uri, handle);
  }
};

const handleProfileClick = (handle: string) => {
  emit('close');
  emit('view-profile', handle);
};

onMounted(() => {
  fetchNotifications();
  // Mark as seen when opened
  updateNotificationsSeen();
});
</script>

<style scoped>
.notifications-list {
  width: 350px;
  max-width: 90vw;
}

.notification-item {
  transition: background-color 0.2s;
  cursor: pointer;
}

.notification-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.unread-item {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.border-bottom {
  border-bottom: 1px solid currentColor;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.border-white-01 {
  border-color: rgba(255, 255, 255, 0.02) !important;
}

.notification-title {
  font-size: 0.75rem !important;
  line-height: 1.2;
  white-space: normal;
}

.notification-subtitle {
  font-size: 0.7rem !important;
  line-height: 1.2;
  white-space: normal;
}

.italic {
  font-style: italic;
}

.avatar-stack {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.stacked-avatar {
  border: 2px solid rgb(var(--v-theme-surface));
  transition: transform 0.2s;
}

.stacked-avatar:hover {
  transform: translateY(-2px);
  z-index: 20 !important;
}

.more-authors {
  white-space: nowrap;
}
</style>
